import {
  addEdge,
  useReactFlow,
  Handle,
  Position,
  Edge,
  reconnectEdge,
  Connection,
  MarkerType,
} from '@xyflow/react'
import React, { useCallback, useRef, useEffect } from 'react'

export const GhostNode: React.FC = () => (
  <div style={{ width: 5, height: 5 }}>
    <Handle
      type="target"
      position={Position.Top}
      isConnectable={false}
      style={{ background: '#234245', opacity: 0.0 }}
    />
  </div>
)

export function useIncompleteEdge() {
  const { setNodes, setEdges, screenToFlowPosition } = useReactFlow()

  const onConnect = useCallback(
    (params: Connection | Edge) => {
      console.log('[useIncompleteEdge.onConnect]', params)
      if (params.source && params.target) {
        const edge: Edge = {
          id: `${params.source}-${params.target}`,
          source: params.source,
          target: params.target,
          type: params.type || 'annotationedge',
          // Add any other required properties
        }
        setEdges((eds) => addEdge(edge, eds))
      }
    },
    [setEdges]
  )
  const onConnectEnd = useCallback(
    (event, connectionState) => {
      console.log('[useIncompleteEdge.onConnectEnd]', event, connectionState)

      // Prevent connections to/from ghost nodes
      if (
        connectionState.fromNode?.type == 'ghost' || // Ghost node as source
        connectionState.toNode?.type == 'ghost' || // Ghost node as target
        connectionState.toHandle !== null // Any valid target handle
      ) {
        console.log(
          'Connection involving anything else than ghost node is not allowed'
        )
        return
      }

      if (
        connectionState.isValid ||
        connectionState.fromHandle.type === 'target'
      ) {
        return
      }

      if (
        !connectionState.fromNode ||
        connectionState.fromNode.type !== 'annotationnode'
      ) {
        return // Exit if it's not an annotation node
      }

      if (
        connectionState.toHandle !== null ||
        connectionState.toNode !== null ||
        connectionState.toPosition !== null
      ) {
        return // Exit if it's not an annotation node
      }

      const fromNodeId = connectionState.fromNode.id
      const id = `ghost-${Date.now()}`
      const { clientX, clientY } =
        'changedTouches' in event ? event.changedTouches[0] : event
      const newNode = {
        id,
        type: 'ghost',
        position: screenToFlowPosition({
          x: clientX,
          y: clientY,
        }),
        data: {},
      }

      const newEdge: Edge = {
        id: `${fromNodeId}->${id}`,
        source: fromNodeId,
        target: id,
        reconnectable: true,
        markerEnd: {
          type: MarkerType.ArrowClosed,
          width: 20,
          height: 20,
          color: '#DDD',
        },
        type: 'annotationedge', // You can change this to any other valid edge type
      }

      console.log(
        '[useIncompleteEdge.onConnectEnd].newNode,newEdge.',
        newNode,
        newEdge
      )
      setNodes((nodes) => nodes.concat(newNode))
      setEdges((edges) => addEdge(newEdge, edges))
    },
    [setNodes, setEdges, screenToFlowPosition]
  )

  const onReconnect = useCallback(
    (oldEdge, newConnection) => {
      /* console.log(
        '[useIncompleteEdge.onReconnect].oldEdge,newConnection.',
        oldEdge,
        newConnection
      ) */
      setEdges((edges) => reconnectEdge(oldEdge, newConnection, edges))
    },
    [setEdges]
  )

  const onReconnectEnd = useCallback(
    (_, oldEdge, handleType) => {
      console.log(
        '[useIncompleteEdge.onReconnectEnd].oldEdge,handleType.',
        oldEdge,
        handleType
      )
      if (handleType === 'source') {
        setNodes((nodes) => {
          return nodes.filter((node) => {
            const isGhost = node.type === 'ghost'
            const isTarget = node.id === oldEdge.target

            return !(isGhost && isTarget)
          })
        })

        setEdges((edges) => edges.filter((edge) => edge.id !== oldEdge.id))
      }
    },
    [setNodes, setEdges]
  )

  const onEdgesDelete = useCallback(
    (deletedEdges) => {
      console.log(
        '[useIncompleteEdge.onReconnectEnd].deletedEdges.',
        deletedEdges
      )
      setNodes((nodes) => {
        return deletedEdges.reduce(
          (acc, edge) =>
            acc.filter((n) => {
              const isGhost = n.type === 'ghost'
              const isSourceOrTarget =
                n.id === edge.source || n.id === edge.target

              return !(isGhost && isSourceOrTarget)
            }),
          nodes
        )
      })
    },
    [setNodes]
  )

  return {
    onConnect,
    onConnectEnd,
    onReconnect,
    onReconnectEnd,
    onEdgesDelete,
  }
}
